import { useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { Badge, TableCard, type BadgeTone } from './ui'
import type { DeviceInstallStatus } from '../services/api'
import { dateTime } from '../utils/date'

/**
 * How a deployment actually landed, device by device.
 *
 * Shared by the application deployment page and the branding one because both ask Intune the same
 * question — reports/DeviceInstallStatusByApp on an application id — and a second copy of this
 * table would eventually answer it differently on one of the two pages.
 *
 * `statuses` is null when Intune could not be asked. That is NOT an empty estate, and the two are
 * rendered differently on purpose: an empty table under a heading reads as "no devices have this",
 * which is a claim about the customer's machines rather than about our access to them.
 *
 * A row carrying `assignedVia` says which assignment target brought the device here, and the column
 * appears only for callers that resolve one. A branding deployment does: its rows cover the whole
 * audience, so a machine that has reported nothing still has a row and the column is the only thing
 * saying why it is listed. An application deployment lists what the report carried, where every row
 * is a device that reported and the column would be empty on all of them.
 */

/** mobileAppInstallStatus, as the table reads it out. */
const INSTALL_STATE_DISPLAY: Record<string, { label: string; tone: BadgeTone }> = {
  installed: { label: 'Installed', tone: 'green' },
  pendinginstall: { label: 'Pending', tone: 'amber' },
  notinstalled: { label: 'Pending', tone: 'amber' },
  failed: { label: 'Failed', tone: 'red' },
  uninstallfailed: { label: 'Uninstall failed', tone: 'red' },
  notapplicable: { label: 'Not applicable', tone: 'slate' },
}

export function installStateDisplay(state: string | null): { label: string; tone: BadgeTone } {
  // `unknown` is Intune not having reported yet, which is not "not applicable" — the counts above
  // these tables keep them apart and so must this.
  return (
    INSTALL_STATE_DISPLAY[(state || '').toLowerCase()] ?? { label: 'Not reported', tone: 'slate' }
  )
}

const thCls = 'px-4 py-2 text-[12px] font-medium text-theme-text-tertiary'

/** What put this device in the audience, as one line. A tenant-wide target names no group, so it is
 *  labelled rather than left blank; a group Entra would not name keeps its id, because the
 *  assignment still points at it. */
function assignedViaLabel(via: NonNullable<DeviceInstallStatus['assignedVia']>): string {
  return via
    .map((t) =>
      t.type === 'allDevices'
        ? 'All devices'
        : t.type === 'allUsers'
          ? 'All users'
          : t.name || t.id || 'Unnamed group',
    )
    .join(', ')
}

/** Uninstall targets are worth calling out on the row: the device is in the audience to have the
 *  branding REMOVED, which the install status alone reads as a rollout going backwards. */
function uninstallOnly(via: NonNullable<DeviceInstallStatus['assignedVia']>): boolean {
  return via.length > 0 && via.every((t) => t.intent === 'uninstall')
}

export default function DeviceInstallStatusTable({
  statuses,
  unavailableNote,
  pageSize = 25,
}: {
  statuses: DeviceInstallStatus[] | null | undefined
  /** What to say when Intune could not be asked. The caller knows whether that was a missing
   *  connection or a failed report, and the two are worth different sentences. */
  unavailableNote?: string | null
  pageSize?: number
}) {
  const [showAll, setShowAll] = useState(false)

  if (statuses == null) {
    return (
      <p className="rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-3 text-sm text-theme-text-secondary">
        {unavailableNote ||
          'Intune could not be asked for per-device status. That is not the same as no devices.'}
      </p>
    )
  }

  const shown = showAll ? statuses : statuses.slice(0, pageSize)
  // Only when a caller resolved one. The column is dead weight on a table whose every row is a
  // device that reported, and misleading on one row of a table where no other row has it.
  const showAudience = statuses.some((d) => d.assignedVia !== undefined)
  const columns = showAudience ? 6 : 5

  return (
    <div>
      <TableCard
        footer={
          <span>
            Showing {shown.length} of {statuses.length} devices
          </span>
        }
      >
        <thead>
          <tr className="border-b border-theme-border-primary text-left">
            <th className={thCls}>Device</th>
            {showAudience && <th className={thCls}>Assigned via</th>}
            <th className={thCls}>User</th>
            <th className={thCls}>OS</th>
            <th className={thCls}>Status</th>
            <th className={thCls}>Last check-in</th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 ? (
            <tr>
              <td
                colSpan={columns}
                className="px-4 py-6 text-center text-sm text-theme-text-tertiary"
              >
                No device has reported on this yet.
              </td>
            </tr>
          ) : (
            shown.map((d, i) => {
              const state = installStateDisplay(d.installState)
              return (
                <tr
                  key={d.deviceId ?? `${d.deviceName}-${i}`}
                  className="border-b border-theme-border-primary last:border-0"
                >
                  <td className="px-4 py-2">
                    <div className="text-[13px] font-medium text-theme-text-primary">
                      {d.deviceName || '—'}
                    </div>
                    {/* The version this device actually has. The install report carries no device
                        model, and which version landed is the more useful answer here anyway. */}
                    {d.appVersion && (
                      <div className="text-[11px] text-theme-text-tertiary">v{d.appVersion}</div>
                    )}
                  </td>
                  {showAudience && (
                    <td className="px-4 py-2 text-[13px] text-theme-text-secondary">
                      {d.assignedVia?.length ? (
                        <>
                          {assignedViaLabel(d.assignedVia)}
                          {uninstallOnly(d.assignedVia) && (
                            <div className="text-[11px] text-theme-text-tertiary">
                              assigned to uninstall
                            </div>
                          )}
                        </>
                      ) : (
                        /* Reported on the application while outside the audience: it holds this
                           branding and nothing currently sends it there. */
                        <span className="text-theme-text-tertiary">
                          Not assigned
                          {d.inAudience === false && (
                            <span className="block text-[11px]">holds it anyway</span>
                          )}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-2 text-[13px] text-theme-text-secondary">
                    {d.user || '—'}
                  </td>
                  <td className="px-4 py-2 text-[13px] text-theme-text-secondary">{d.os || '—'}</td>
                  <td className="px-4 py-2">
                    <Badge tone={state.tone}>{state.label}</Badge>
                  </td>
                  <td className="px-4 py-2 text-[13px] text-theme-text-secondary">
                    {d.lastCheckIn ? dateTime(d.lastCheckIn) : '—'}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </TableCard>
      {statuses.length > shown.length && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-theme-accent-blue hover:underline"
        >
          <ChevronDownIcon className="h-3.5 w-3.5" />
          Show all {statuses.length} devices
        </button>
      )}
    </div>
  )
}
